import { useRef, useMemo, useCallback, useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import _ from "lodash"

import {
  DEFAULT_FILTER_VALUE,
  Q_SUB_CATEGORY_ID,
  Q_ORDINAL,
  Q_PARENT_CATEGORY_ID,
  Q_VENUE,
  GTM_VIEW_DIRECTORY_CATEGORY,
  DIRECTORY_PAGE_CONFIG,
} from "constant"

import { useVenue } from "providers/venue"
import { useTranslation } from "../../providers/i18n/useTranslation"
import { useGeoLocation } from "providers/geolocation"
import { useGetOccupants } from "providers/venue/modules/data/occupants/useGetOccupants"
import { useVenueController } from "hooks/venue/useVenueController"
import { useInfiniteScroll } from "../useInfiniteScroll"
import { useInfiniteGetOccupants } from "providers/venue/modules/data/occupants/useInfiniteGetOccupants"

import { sortFeatureByLocaleKey } from "../../utils/sortFeatureByLocaleKey"
import {
  createSortingConfigByOrdinalAndName,
  createSortingConfigFeatureByName,
  getFeatureDetail,
} from "utils/feature"
import { pushDataLayer } from "utils/googletagmanager"
import { useRecommendedDirectory } from "hooks/sponsored-content/recommended"
import { processOccupantData } from "hooks/directory/util"
import { useGetPageConfig } from "providers/venue/modules/data"

const DEFAULT_LEVEL_OBJECT = {
  id: "all",
  name: "All Floors",
  value: DEFAULT_FILTER_VALUE,
}

export const useDirectory = () => {
  const { locationVenue } = useGeoLocation()
  const locationVenueId = _.get(locationVenue, "id")
  const { viewingVenue: venueFilter } = useVenueController()

  const navigate = useNavigate()
  const {
    occupants: venueOccupant,
    levels,
    venues,
    isMultiVenueProject,
    isMultiOrdinalVenue,
    findFeatureById,
    dataLoaded,
  } = useVenue()

  const { layout = "list", metadata } = useGetPageConfig(DIRECTORY_PAGE_CONFIG)

  const {
    i18n: { language: chosenLanguage },
  } = useTranslation()
  const [searchParams] = useSearchParams()
  const ref = useRef()

  const defaultVenueFilter = useMemo(
    () => locationVenueId || DEFAULT_FILTER_VALUE,
    [locationVenueId]
  )

  const { recommendedDirectories } = useRecommendedDirectory()

  // Query String filters
  const parentCategoryId = useMemo(
    () => searchParams.get(Q_PARENT_CATEGORY_ID) || DEFAULT_FILTER_VALUE,
    [searchParams]
  )

  const subCategoryId = useMemo(
    () => searchParams.get(Q_SUB_CATEGORY_ID) || DEFAULT_FILTER_VALUE,
    [searchParams]
  )

  const ordinalFilter = useMemo(() => {
    const ordinalParam = searchParams.get(Q_ORDINAL)
    const ordinal = Number(ordinalParam)
    return !_.isNil(ordinalParam) && !_.isNaN(ordinal)
      ? ordinal
      : DEFAULT_FILTER_VALUE
  }, [searchParams])

  const sortingConfig = useMemo(() => {
    const sortingStrategies = {
      group: () => createSortingConfigByOrdinalAndName(chosenLanguage),
      default: () => createSortingConfigFeatureByName(chosenLanguage),
    }

    return (sortingStrategies[layout] || sortingStrategies.default)()
  }, [layout, chosenLanguage])

  // Filter occupants with an available anchor
  const { data: availableOccupants } = useGetOccupants({
    pagination: {
      perPage: _.size(venueOccupant),
    },
    filter: {
      properties: {
        anchor: {
          $exists: true,
        },
      },
    },
    enabled: dataLoaded,
  })

  // Filter occupants with a parentCategoryId.
  const { data: occupantsFilteredByParentCategory } = useGetOccupants({
    pagination: {
      perPage: _.size(availableOccupants),
    },
    filter: {
      properties: {
        local_parent_categories: {
          $elemMatch: {
            id: parentCategoryId,
          },
        },
      },
    },
    enabled: !!availableOccupants,
  })

  /**
   * Result All Filtered Occupants (without ordinal filter)
   * - Used to create an array of unique levels of occupants that match the category
   * - Used as the source data for the useInfiniteGetOccupants hook
   */
  const { data: allfilteredOccupantsSorted } = useGetOccupants({
    pagination: {
      perPage: _.size(availableOccupants),
    },
    sort: sortingConfig,
    filter: {
      properties: {
        local_parent_categories: {
          $elemMatch: {
            id: parentCategoryId,
          },
        },
        local_categories: {
          $elemMatch: {
            id: subCategoryId,
          },
        },
        venue: {
          id: venueFilter,
        },
      },
    },
    enabled: !!occupantsFilteredByParentCategory,
  })

  /**
   * Result Infinite Filtered Occupants
   * - Used to display data in an infinite scroll version
   */
  const {
    data: filteredOccupantsSorted,
    hasNextPage,
    isFetched,
    fetchNextPage,
  } = useInfiniteGetOccupants({
    pagination: {
      perPage: 25,
    },
    sort: sortingConfig,
    filter: {
      properties: {
        local_parent_categories: {
          $elemMatch: {
            id: parentCategoryId,
          },
        },
        local_categories: {
          $elemMatch: {
            id: subCategoryId,
          },
        },
        venue: {
          id: venueFilter,
        },
        ordinal: ordinalFilter,
        is_maintenance: {
          $ne: true,
        },
      },
    },
    select: (data) => processOccupantData(data, metadata),
    enabled: !!allfilteredOccupantsSorted,
  })

  const occupantsWithSponsoredContent = useMemo(() => {
    // Early return: no data or no sponsored content
    if (!filteredOccupantsSorted || _.isEmpty(recommendedDirectories)) {
      return filteredOccupantsSorted
    }

    // Step 1: Filter sponsored content by parent & sub category
    const matchedSponsoredContent = recommendedDirectories.filter(
      (sponsoredContent) => {
        const matchedParentCategory =
          parentCategoryId === DEFAULT_FILTER_VALUE ||
          _.get(
            sponsoredContent,
            "properties.local_parent_categories",
            []
          ).some((cat) => cat.id === parentCategoryId)

        const matchedSubCategory =
          subCategoryId === DEFAULT_FILTER_VALUE ||
          _.get(sponsoredContent, "properties.local_categories", []).some(
            (cat) => cat.id === subCategoryId
          )

        return matchedParentCategory && matchedSubCategory
      }
    )

    // Step 2: Remove occupants that have same ID as sponsored content
    const sponsoredContentIds = new Set(
      matchedSponsoredContent.map((content) => content.id)
    )
    const removeDuplicatesOccupant = (occupants) =>
      occupants.filter((occupant) => !sponsoredContentIds.has(occupant.id))

    // Step 3: Insert sponsored content based on layout
    if (layout === "list") {
      // List layout: Put all sponsored content at the top of first page
      return {
        ...filteredOccupantsSorted,
        pages: filteredOccupantsSorted.pages.map((page, pageIndex) => {
          const cleanedOccupants = removeDuplicatesOccupant(page.data || [])
          const isFirstPage = pageIndex === 0

          return {
            ...page,
            data: isFirstPage
              ? [...matchedSponsoredContent, ...cleanedOccupants]
              : cleanedOccupants,
          }
        }),
      }
    }

    // Group layout: Put sponsored content at the start of each ordinal (once per ordinal)
    const sponsoredContentByOrdinal = _.groupBy(
      matchedSponsoredContent,
      "properties.ordinal"
    )
    const processedOrdinals = new Set()

    return {
      ...filteredOccupantsSorted,
      pages: filteredOccupantsSorted.pages.map((page) => {
        // Group occupants by ordinal
        const occupantsByOrdinal = _.groupBy(
          page.data || [],
          "properties.ordinal"
        )

        // Sort ordinals descending and merge with sponsored content
        const mergedData = _.orderBy(
          Object.keys(occupantsByOrdinal).map(Number),
          [],
          ["desc"]
        ).flatMap((ordinal) => {
          const occupants = removeDuplicatesOccupant(
            occupantsByOrdinal[ordinal]
          )
          const sponsoredContentForOrdinal =
            sponsoredContentByOrdinal[ordinal] || []

          // Insert sponsored content only once per ordinal (across all pages)
          const shouldInsertSponsoredContent =
            sponsoredContentForOrdinal.length > 0 &&
            !processedOrdinals.has(ordinal)

          if (shouldInsertSponsoredContent) {
            processedOrdinals.add(ordinal)
            return [...sponsoredContentForOrdinal, ...occupants]
          }

          return occupants
        })

        return { ...page, data: mergedData }
      }),
    }
  }, [
    filteredOccupantsSorted,
    recommendedDirectories,
    layout,
    parentCategoryId,
    subCategoryId,
  ])

  const onReachEndScroll = useCallback(
    (inView) => {
      if (inView && hasNextPage) fetchNextPage()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasNextPage]
  )

  const { endScrollRef } = useInfiniteScroll({
    onReachEndScroll,
  })

  // Prepare parent category by available occupants
  const parentCategoryWithOccupant = useMemo(
    () =>
      _(availableOccupants)
        .map("properties.local_parent_categories")
        .compact()
        .flatten()
        .uniqBy("id")
        .value(),
    [availableOccupants]
  )

  const sortedParentCategoryWithOccupant = useMemo(
    () =>
      _.orderBy(
        parentCategoryWithOccupant,
        ["properties.is_featured", "properties.order"],
        ["desc", "asc"]
      ),
    [parentCategoryWithOccupant]
  )

  // Prepare subcategories by filtering parent category occupants.
  const subCategoryWithActiveParentCategory = useMemo(
    () =>
      _.isNil(parentCategoryId)
        ? []
        : _(occupantsFilteredByParentCategory)
            .map("properties.local_categories")
            .compact()
            .flatten()
            .uniqBy("id")
            .filter(
              (category) =>
                _.get(category, "properties.parent_id") === parentCategoryId
            )
            .sortBy("properties.order")
            .value(),
    [parentCategoryId, occupantsFilteredByParentCategory]
  )

  // Prepare venue options.
  const sortedVenues = useMemo(() => {
    return sortFeatureByLocaleKey(venues, "properties.name", chosenLanguage)
  }, [chosenLanguage, venues])

  const venueOptions = useMemo(() => {
    const defaultVenueObject = {
      id: "all-venues",
      name: "All Venues",
      value: DEFAULT_FILTER_VALUE,
    }
    const options = sortedVenues.map((venue) => {
      const { id, name } = getFeatureDetail(venue)
      return { id, name, value: id }
    })

    return [defaultVenueObject, ...options]
  }, [sortedVenues])

  // Array of unique levels of occupants who matched with category
  const filteredLevels = useMemo(
    () =>
      _(allfilteredOccupantsSorted)
        .uniqBy("properties.ordinal")
        .filter((item) => !_.isNil(item.properties.ordinal))
        .map(({ properties: { ordinal } }) =>
          levels.find((level) => level.properties.ordinal === ordinal)
        )
        .orderBy("properties.ordinal", "asc")
        .value(),
    [levels, allfilteredOccupantsSorted]
  )

  const levelOptions = useMemo(() => {
    const options = filteredLevels.map((level) => {
      const { id, name, ordinal } = getFeatureDetail(level)
      return { id, name, value: ordinal }
    })

    return [DEFAULT_LEVEL_OBJECT, ...options]
  }, [filteredLevels])

  const hasData = _.size(filteredOccupantsSorted?.pages[0]?.data) > 0
  const hasParentCategory = _.size(sortedParentCategoryWithOccupant) > 0

  const scrollToRef = useCallback(
    () => ref.current?.scrollIntoView({ behavior: "smooth" }),
    [ref]
  )

  const handleChangeOrdinal = useCallback(
    (e, id) => {
      if (id === null) return
      searchParams.set(Q_ORDINAL, id)
      navigate(`/directory?${searchParams.toString()}`, { replace: true })
      scrollToRef()
    },
    [scrollToRef, searchParams, navigate]
  )

  const changeVenueFilter = useCallback(
    (venueId = defaultVenueFilter) => {
      searchParams.set(Q_VENUE, venueId)
      navigate(`/directory?${searchParams.toString()}`, { replace: true })
      scrollToRef()
    },
    [scrollToRef, searchParams, navigate, defaultVenueFilter]
  )

  const handleSelectLevelChange = useCallback(
    (e) => {
      const ordinal = e.target.value
      handleChangeOrdinal(e, ordinal)
    },
    [handleChangeOrdinal]
  )

  const handleChangeParentCategory = useCallback(
    (e, id) => {
      if (id === null) return
      searchParams.set(Q_PARENT_CATEGORY_ID, id)
      searchParams.set(Q_SUB_CATEGORY_ID, DEFAULT_FILTER_VALUE)
      searchParams.set(Q_ORDINAL, DEFAULT_FILTER_VALUE)
      navigate(`/directory?${searchParams.toString()}`, { replace: true })
    },
    [navigate, searchParams]
  )

  const handleChangeSubCategory = useCallback(
    (e, id) => {
      if (id === null) return
      searchParams.set(Q_SUB_CATEGORY_ID, id)
      searchParams.set(Q_ORDINAL, DEFAULT_FILTER_VALUE)
      navigate(`/directory?${searchParams.toString()}`, { replace: true })
    },
    [navigate, searchParams]
  )

  const resetFilter = useCallback(() => {
    searchParams.set(Q_PARENT_CATEGORY_ID, DEFAULT_FILTER_VALUE)
    searchParams.set(Q_SUB_CATEGORY_ID, DEFAULT_FILTER_VALUE)
    searchParams.set(Q_VENUE, DEFAULT_FILTER_VALUE)
    searchParams.set(Q_ORDINAL, DEFAULT_FILTER_VALUE)
    navigate(`/directory?${searchParams.toString()}`, { replace: true })
  }, [navigate, searchParams])

  const showSubCategoryFilter = useMemo(
    () =>
      subCategoryWithActiveParentCategory.length > 0 &&
      parentCategoryId !== DEFAULT_FILTER_VALUE,
    [subCategoryWithActiveParentCategory, parentCategoryId]
  )

  useEffect(() => {
    try {
      if (!!parentCategoryId && parentCategoryId !== DEFAULT_FILTER_VALUE) {
        const categoryName = parentCategoryWithOccupant.find(
          (taxonomy) => taxonomy.id === parentCategoryId
        )?.properties?.name?.en
        return pushDataLayer({
          event: GTM_VIEW_DIRECTORY_CATEGORY,
          directory_category_id: parentCategoryId,
          directory_category_name: categoryName,
          directory_subcategory_name:
            subCategoryId === DEFAULT_FILTER_VALUE
              ? DEFAULT_FILTER_VALUE
              : _.get(findFeatureById(subCategoryId), "properties.name.en"),
        })
      }
    } catch (err) {
      console.log(err)
    }
  }, [
    parentCategoryId,
    parentCategoryWithOccupant,
    subCategoryId,
    findFeatureById,
  ])

  return {
    handleChangeParentCategory,
    handleChangeSubCategory,
    handleChangeOrdinal,
    handleSelectLevelChange,
    changeVenueFilter,
    resetFilter,
    endScrollRef,
    hasNextPage,
    filteredOccupantsSorted: occupantsWithSponsoredContent,
    filteredLevels,
    parentCategoryWithOccupant,
    sortedParentCategoryWithOccupant,
    subCategoryWithActiveParentCategory,
    parentCategoryId,
    subCategoryId,
    ordinalFilter,
    venueFilter,
    levelOptions,
    venueOptions,
    defaultOrdinalFilterValue: DEFAULT_FILTER_VALUE,
    defaultCategoryFilterValue: DEFAULT_FILTER_VALUE,
    defaultVenueFilterValue: DEFAULT_FILTER_VALUE,
    ref,
    venues,
    isMultiVenueProject,
    isMultiOrdinalVenue,
    locationVenueId,
    showSubCategoryFilter,
    hasData,
    isFetched,
    hasParentCategory,
    layout,
  }
}
